import React, { forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { gsap, Power0 } from 'gsap';
import { Link } from 'react-router-dom';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

import { mediaQueries } from '@pkg/providers';

import { type ButtonBaseProps, Colors, Variants } from './types';

import { StyledButtonBase } from './styles';

export const ButtonBase = forwardRef(
    (
        { children, to, disabled, color = Colors.primary, variant = Variants.yat, ...rest }: ButtonBaseProps,
        forwardedRef,
    ) => {
        const theme = useTheme();
        const usingMouse = useMedia(mediaQueries.usingMouse);

        const colorPrimaryOrSuccess = color === Colors.primary || color === Colors.success;

        const ref = useRef(null);
        const mouseXPercentage = useRef<string>(colorPrimaryOrSuccess ? '100%' : '50%');
        const mouseYPercentage = useRef<string>('50%');

        useImperativeHandle(
            forwardedRef,
            () => {
                if (forwardedRef) {
                    return ref.current;
                }
            },
            [forwardedRef],
        );

        const animateBackgroundGradient = useCallback(
            (hovering: boolean, duration = 0.5) => {
                let base: string;
                let gradient: string;
                let shadow: string | undefined = undefined;

                if (variant !== Variants.yat) return;

                switch (color) {
                    case Colors.primary: {
                        base = theme.colors.black;
                        gradient = hovering ? theme.colors.blue : theme.colors.purple;
                        break;
                    }
                    case Colors.success: {
                        base = theme.colors.black;
                        gradient = hovering ? theme.colors.greenDark : theme.colors.green;
                        break;
                    }
                    case Colors.error: {
                        base = theme.colors.black;
                        gradient = theme.colors.red;
                        break;
                    }
                    case Colors.translucent: {
                        base = hovering ? theme.shades.white[20] : theme.shades.white[10];
                        gradient = theme.shades.white[5];
                        shadow = `0 0 15px ${theme.shades.white[35]}, inset 0 0 20px ${
                            hovering ? theme.shades.white[10] : 'transparent'
                        }`;
                        break;
                    }
                    case Colors.white: {
                        base = theme.colors.white;
                        gradient = theme.shades.white[5];
                        break;
                    }
                    case Colors.gradient: {
                        base = '#3f3b58';
                        gradient = '#221a25';
                        break;
                    }
                    case Colors.clear: {
                        base = 'transparent';
                        gradient = 'transparent';
                        break;
                    }
                    case Colors.secondary:
                    default: {
                        base = hovering ? theme.shades.white[20] : theme.shades.white[10];
                        gradient = theme.shades.white[5];
                        shadow = `inset 0 0 20px ${hovering ? theme.shades.white[10] : 'transparent'}`;
                        break;
                    }
                }

                /*
                This gsap animation was really broken on Firefox, we might want to revisit this in the
                future and change from gsap to framer motion or plain javascript mouse x/y
                */

                const isFirefox = navigator.userAgent.indexOf('Firefox');

                if (isFirefox === -1) {
                    gsap.to(ref.current, {
                        background: `radial-gradient(circle at ${mouseXPercentage.current} ${mouseYPercentage.current}, ${base} 0%, ${gradient} 100%)`,
                        boxShadow: shadow,
                        duration: duration,
                        ease: Power0.easeNone,
                    });
                }
            },
            [theme, variant, color],
        );

        const handleMouseMove = (event) => {
            if (rest.onMouseOver) rest.onMouseOver(event);
            if (!ref.current || !usingMouse) return;

            const current = ref.current as unknown as HTMLAnchorElement | HTMLButtonElement;
            const { left: x, top: y } = current.getBoundingClientRect();

            const width = current.clientWidth;
            const height = current.clientHeight;

            const buttonX = Math.floor(event.clientX - x);
            const buttonY = Math.floor(event.clientY - y);

            mouseXPercentage.current = `${(buttonX / width) * 100}%`;
            mouseYPercentage.current = `${(buttonY / height) * 100}%`;

            animateBackgroundGradient(true);
        };

        const handleMouseLeave = (event) => {
            if (rest.onMouseLeave) rest.onMouseLeave(event);
            if (!ref.current || !usingMouse) return;

            // Reset background to default position
            if (colorPrimaryOrSuccess) {
                mouseXPercentage.current = '100%';
                mouseYPercentage.current = '50%';
            }

            animateBackgroundGradient(false);
        };

        // Reset to initial position on load
        useEffect(() => animateBackgroundGradient(false, 0), [animateBackgroundGradient]);

        const sharedProps = {
            $color: color,
            $variant: variant,
            onMouseMove: handleMouseMove,
            onMouseLeave: handleMouseLeave,
        };

        return to && !disabled ? (
            <StyledButtonBase ref={ref as Ref<HTMLAnchorElement>} as={Link} to={to} {...sharedProps} {...rest}>
                {children}
            </StyledButtonBase>
        ) : (
            <StyledButtonBase ref={ref as Ref<HTMLButtonElement>} {...sharedProps} disabled={disabled} {...rest}>
                {children}
            </StyledButtonBase>
        );
    },
);

ButtonBase.displayName = 'ButtonBase';
